import React, { useState } from 'react';
import PropTypes from 'prop-types';
import tw, { styled } from 'twin.macro';

import {
  Button,
  Flex,
  Label,
  TextInput,
  Toggle,
  useNotification,
  Span
} from '@sopranodesign/pulse-components';
import { useMutation, useQueryClient } from '@sopranodesign/pulse-hooks';

import { updateSettings as updateSettingsApi } from '../api/index';

const WhatsAppDataForm = ({
  number,
  bsuid,
  token,
  prevBaseUri,
  noSecret
}) => {
  const [useBsuid, setUseBsuid] = useState(Boolean(bsuid));

  const [phoneNumber, setPhoneNumber] = useState(number ?? '');

  const [bsuidValue, setBsuidValue] = useState(bsuid ?? '');

  const [apiId, setApiId] = useState(token ?? '');

  const [apiKey, setApiKey] = useState('');

  const [baseUri, setBaseUri] = useState(prevBaseUri ?? '');

  const [showApiKey, setShowApiKey] = useState(noSecret);

  const { showSuccess } = useNotification();

  const queryClient = useQueryClient();

  const { mutate: updateSettings } = useMutation({
    mutationFn: updateSettingsApi,
    onSuccess: () => {
      showSuccess('Successfully updated whatsApp settings');
      queryClient.invalidateQueries({
        queryKey: ['whatsapp/settings']
      });
    }
  });

  const handlePhoneNumberChange = event => {
    setPhoneNumber(event.target.value);
  };

  const handleBsuidChange = event => {
    setBsuidValue(event.target.value);
  };

  const handleIdentifierModeToggle = () => setUseBsuid(previous => !previous);

  const handleBaseUriChange = event => {
    setBaseUri(event.target.value);
  };

  const handleApiIdChange = event => {
    setApiId(event.target.value);
  };

  const handleApiKeyChange = event => {
    setApiKey(event.target.value);
  };

  const handelSubmit = () => {
    const identifier = useBsuid
      ? { bsuid: bsuidValue }
      : { phone_number: phoneNumber };

    if (showApiKey) {
      updateSettings({
        ...identifier,
        api_id: apiId,
        api_key: apiKey,
        base_uri: baseUri
      });
    } else {
      updateSettings({
        ...identifier,
        api_id: apiId,
        base_uri: baseUri
      });
    }
  };

  const handleShowClientKey = () => setShowApiKey(true);

  const ActionsContainer = styled.div`${tw`flex p-4 md:p-0`}`;

  return (
    <Flex col>
      <Flex col mb>
        <Label>Identifier Type</Label>
        <ActionsContainer>
          <Toggle
            title="whatsapp-identifier"
            data-testid="whatsapp-identifier-toggle"
            options={['MSISDN', 'BSUID']}
            on={useBsuid}
            handleToggleChange={handleIdentifierModeToggle}
          />
        </ActionsContainer>
      </Flex>
      {useBsuid ? (
        <Flex col mb>
          <Label htmlFor="whatsapp_bsuid_input">BSUID</Label>
          <TextInput
            id="whatsapp_bsuid_input"
            data-testid="whatsapp-bsuid-input"
            value={bsuidValue}
            onChange={handleBsuidChange}
            placeholder="US.13491208655302741918"
          />
        </Flex>
      ) : (
        <Flex col mb>
          <Label htmlFor="whatsapp_number_input">Phone Number</Label>
          <TextInput
            id="whatsapp_number_input"
            data-testid="whatsapp-number-input"
            value={phoneNumber}
            onChange={handlePhoneNumberChange}
            placeholder="447451123456"
          />
        </Flex>
      )}
      <Flex col mb>
        <Label htmlFor="whatsapp_base_uri_input">Base Url</Label>
        <TextInput
          id="whatsapp_base_uri_input"
          data-testid="whatsapp-base-uri-input"
          value={baseUri}
          onChange={handleBaseUriChange}
          placeholder="https://uk.sopranodesign.com"
        />
      </Flex>
      <Flex col mb>
        <Label htmlFor="whatsapp_id_input">Api Id</Label>
        <TextInput
          id="whatsapp_id_input"
          data-testid="whatsapp-id-input"
          value={apiId}
          onChange={handleApiIdChange}
          placeholder="Your api id"
        />
      </Flex>
      <Flex col mb>
        <Label htmlFor="whatsapp_key_input">Api Key</Label>
        <Flex between center>
          {showApiKey ? (
            <TextInput
              id="whatsapp_key_input"
              data-testid="whatsapp-key-input"
              value={apiKey}
              onChange={handleApiKeyChange}
              placeholder="Your api key"
            />
          ) : (
            <Span>Api key credentials have been set</Span>
          )}
          {!showApiKey && (
            <Flex top>
              <Button
                onClick={handleShowClientKey}
                data-testid="change-whatsapp-key">
                Change Credentials
              </Button>
            </Flex>
          )}
        </Flex>
      </Flex>
      <Flex right mt>
        <Button
          variant="primary"
          onClick={handelSubmit}
          data-testid="submit-whatsapp-data">
          Save
        </Button>
      </Flex>
    </Flex>
  );
};

WhatsAppDataForm.propTypes = {
  number: PropTypes.string,
  bsuid: PropTypes.string,
  token: PropTypes.string,
  noSecret: PropTypes.bool,
  prevBaseUri: PropTypes.string
};

export default WhatsAppDataForm;
